import React, { useEffect, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import Modal from 'react-native-modal'
import { useTheme } from '@react-navigation/native'
import type { Theme } from '@theme'
import { useLanguage } from '@context'
// Import atoms/molecules directly (not via the @components barrel): the barrel
// pulls BottomModal → @hooks → react-native-toast-message (ESM), which breaks
// under Jest. Same barrel-avoidance the codebase uses elsewhere.
import { Text } from '../../../../components/atoms/text/Text'
import { TextInput } from '../../../../components/molecules/TextInput'
import { Button } from '../../../../components/molecules/Button'

interface DeleteAccountModalProps {
  isVisible: boolean
  isDeleting: boolean
  errorMessage?: string | null
  /** false = account created through an event: no password to confirm with yet. */
  hasPassword?: boolean
  onCreatePassword?: () => void
  onCancel: () => void
  onConfirm: (password: string) => void
}

/**
 * Confirmation modal for account deletion. The API (`DELETE /accounts/me`)
 * requires the account password as a confirmation, so this collects it before
 * the destructive action. Cross-platform (native + web) via react-native-modal.
 */
export const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  isVisible,
  isDeleting,
  errorMessage,
  hasPassword = true,
  onCreatePassword,
  onCancel,
  onConfirm,
}) => {
  const theme = useTheme()
  const styles = getStyles(theme)
  const { t } = useLanguage()
  const [password, setPassword] = useState('')

  // Never let a typed password linger once the modal is dismissed.
  useEffect(() => {
    if (!isVisible) {
      setPassword('')
    }
  }, [isVisible])

  const handleConfirm = (): void => {
    if (!password || isDeleting) {
      return
    }
    onConfirm(password)
  }

  return (
    <Modal
      isVisible={isVisible}
      onBackdropPress={isDeleting ? undefined : onCancel}
      onBackButtonPress={isDeleting ? undefined : onCancel}
      animationIn="fadeIn"
      animationOut="fadeOut"
      statusBarTranslucent
      style={styles.modal}
    >
      <View style={styles.container}>
        <Text type="bodyBold" style={styles.title}>
          {t('settings.deleteAccountConfirmTitle')}
        </Text>
        <Text type="bodySmall" style={styles.message}>
          {t('settings.deleteAccountConfirmMessage')}
        </Text>

        {hasPassword ? (
          <TextInput
            value={password}
            onChangeText={setPassword}
            placeholder={t('settings.deleteAccountPasswordPlaceholder')}
            secureTextEntry
            autoCapitalize="none"
          />
        ) : (
          <Text type="bodySmall" style={styles.needPassword} testID="delete-account-need-password">
            {t('settings.deleteAccountNeedPassword')}
          </Text>
        )}

        {errorMessage ? (
          <Text type="bodySmall" style={styles.error}>
            {errorMessage}
          </Text>
        ) : null}

        {hasPassword ? (
          <Button
            title={
              isDeleting
                ? t('settings.deleteAccountInProgress')
                : t('settings.deleteAccountConfirm')
            }
            isRed
            containerStyle={styles.confirmButton}
            onPress={handleConfirm}
          />
        ) : (
          <Button
            title={t('settings.deleteAccountCreatePassword')}
            testID="delete-account-create-password"
            containerStyle={styles.confirmButton}
            onPress={() => onCreatePassword?.()}
          />
        )}
        <Button
          title={t('settings.deleteAccountCancel')}
          unfilled
          onPress={onCancel}
        />
      </View>
    </Modal>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    modal: {
      justifyContent: 'center',
      paddingHorizontal: theme.spacing.lg,
    },
    container: {
      backgroundColor: theme.colors.white,
      borderRadius: theme.border.medium,
      padding: theme.spacing.xl,
    },
    title: {
      textAlign: 'center',
      color: theme.colors.error,
      marginBottom: theme.spacing.md,
    },
    message: {
      textAlign: 'center',
      marginBottom: theme.spacing.lg,
    },
    needPassword: {
      textAlign: 'center',
    },
    error: {
      color: theme.colors.error,
      marginTop: theme.spacing.sm,
    },
    confirmButton: {
      marginTop: theme.spacing.lg,
      marginBottom: theme.spacing.sm,
    },
  })
